import $ from "jquery";
import {BaseNodeMapping, NodeTypeMapping, errorMapping} from "./nodeMappings";

let previousStyle = {};
const rootDescendants = [];

/**
 * Changes background and border color of the given node to indicate an error
 * @param node Node that contains some kind of error
 */
export function highlightNode(node, invalidNodes) {
    let dataNodeId = $(node).attr("id");
    invalidNodes.push(dataNodeId);
    //Check if the node has been previously highlighted
    if (!previousStyle[dataNodeId]) {
        let style = $(node).children(":first").attr("style");
        let fillColor = "#F26D68";
        let strokeColor = "#6F0000";
        //Store the previous style
        previousStyle[$(node).attr("id")] = style;
        style = style.replace(/fillColor=.*?(?=;)/, 'fillColor=' + fillColor);
        style = style.replace(/strokeColor=.*?(?=;)/, 'strokeColor=' + strokeColor)
        $(node).children(":first").attr('style', style);
    }
}

/**
 * Reverts a node to it previous style
 * @param node Node that no longer contains errors
 */
export function revertNode(node) {
    if (previousStyle[$(node).attr("id")]) {
        $(node).children(":first").attr('style', previousStyle[$(node).attr("id")]);
        delete previousStyle[$(node).attr("id")];
    }
}

/**
 * Creates an array that contains the id all descendants, i.e. all direct or indirect children, of the root node.
 * @returns {*[]} Node id array
 */
export function cacheRootDescendants(templateXml) {
    let root = $(templateXml).find("[nodeType=root]");
    rootDescendants.splice(0, rootDescendants.length);
    getDescendants(root, rootDescendants, templateXml);
    return rootDescendants;
}

/**
 * Adds to the given array all descendants of the given node. The parent-child relationship is checked using the
 * concept of edges provided by diagram.net. Edges are arrow-shaped connectors that, when connecting two nodes,
 * contain both a 'target' and 'source' attribute, corresponding to the id of the parent node and the id of
 * the child node, respectively.
 * https://www.diagrams.net/blog/use-connectors
 * @param node
 * @param descendantsArray Node id array
 */
export function getDescendants(node, descendantsArray, templateXml) {
    let edges = $(templateXml).find("[source=" + $(node).attr("id") + "]");

    for (let i = 0; i < edges.length; i++) {
        let edge = edges[i];
        let child = $(templateXml).find("[id=" + $(edge).attr("target") + "]");
        let childId = $(child).attr("id");
        //Check for possible recursion
        if (!descendantsArray.includes(childId)) {
            descendantsArray.push(childId);
            getDescendants(child, descendantsArray, templateXml);
        }
    }
}

/**
 * Checks if a node is an only child of a given 'nodeType' meaning:
 *  - The node has only one parent
 *  - The parent node has a corresponding nodeType attribute that matches the one given by parameter
 *  - The node is a direct or indirect child of the root (this conditions prevents recursion in the node tree)
 * @param node
 * @param parentNodeType
 * @returns {{result: boolean, error: string}}
 */
export function isOnlyChildOfNodeType(node, parentNodeType, templateXml) {
    let incomingEdge = $(templateXml).find("[target=" + $(node).attr("id") + "]");
    let isNodeValid;

    // No parent or more than one
    if (incomingEdge.length !== 1) {
        if (incomingEdge.length === 0) {
            parentNodeType === NodeTypeMapping.NONE ?
                isNodeValid = {result: true, error: ""} :
                isNodeValid = {result: false, error: errorMapping.SHOULD_HAVE_PARENT};
        } else {
            isNodeValid = {result: false, error: errorMapping.MULTIPLE_PARENTS};
        }
    }
    // Only one parent
    else {
        // Node should have no parents but has one
        if (parentNodeType === NodeTypeMapping.NONE) {
            isNodeValid = {result: false, error: errorMapping.SHOULD_NOT_HAVE_PARENT};
        }
        // Node is not a descendant of the root node
        else if (!rootDescendants.includes($(node).attr("id"))) {
            isNodeValid = {result: false, error: errorMapping.STRAY_NODE};
        } else {
            let parent = $(templateXml).find("[id=" + $(incomingEdge).attr("source") + "]");
            // Node can have any kind of node as a parent
            if (parentNodeType === NodeTypeMapping.ANY && $(parent).attr("nodeType")) {
                isNodeValid = {result: true, error: ""};
            }
            // Node should have a parent with a specific nodeType
            else {
                $(parent).attr("nodeType") === parentNodeType ?
                    isNodeValid = {result: true, error: ""} :
                    isNodeValid = {result: false, error: errorMapping.WRONG_PARENT_TYPE};
            }
        }
    }
    return isNodeValid;
}

/**
 * Checks if a given node has been put inside a container. This check is necessary since containers are being used
 * so that their child nodes can inherit values from them via diagrams.net placeholder functionality:
 * https://www.diagrams.net/blog/placeholders
 * @param node
 * @returns {{result: boolean, error: string}}
 */
export function hasContainerParent(node, templateXml) {
    let containerId = $(node).children(":first").attr("parent");
    let container = $(templateXml).find("[id=" + containerId + "]");
    let isNodeValid = {result: true, error: ""};
    if ($(container).attr("nodeType") !== NodeTypeMapping.CONTAINER) {
        isNodeValid = {result: false, error: errorMapping.MISSING_CONTAINER}
    }
    return isNodeValid;
}

/**
 * Checks whether a node has a valid and unique 'data' attribute
 * @param node
 * @param nodeMapping Map of valid values for the 'data' attribute
 * @returns {{result: boolean, error: string}}
 */
export function isOnlyNodeWithMapping(node, nodeMapping, templateXml) {
    let data = $(node).attr("data");
    let nodesWithSameData = $(templateXml).find('[data=' + data + ']');
    let isNodeValid = {result: true, error: ""};

    if (nodesWithSameData?.length > 1) {
        isNodeValid = {result: false, error: errorMapping.DUPLICATED_NODE};
    }
    if (!Object.values(nodeMapping).includes(data)) {
        isNodeValid = {result: false, error: errorMapping.INVALID_METADATA};
    }

    return isNodeValid;
}

/**
 * Checks whether the root node meets the following conditions:
 *  - There is only one element that has the nodeType attribute set to ROOT
 *  - It has no parent nodes
 * @returns {boolean} true if all conditions are met, false otherwise
 */
export function validateRootNode(templateXml, invalidNodes) {
    let root = $(templateXml).find('[nodeType=' + NodeTypeMapping.ROOT + ']');
    let isNodeValid = {result: false, error: errorMapping.ONLY_ONE_ROOT_ALLOWED};
    if (root && root.length === 1) {
        isNodeValid = isOnlyChildOfNodeType(root, NodeTypeMapping.NONE, templateXml);
        if(isNodeValid.result && $(templateXml).find('[nodeType=' + NodeTypeMapping.TITLE + ']').length === 0){
            isNodeValid = {result: false, error: errorMapping.NO_TITLE};
        }
    }
    for (let i = 0; i < root.length; i++) {
        let node = $(root[i]);
        if (!isNodeValid.result) {
            highlightNode(node, invalidNodes);
        } else {
            revertNode(node);
        }
        $(node).attr("tooltip", isNodeValid.error);
    }
    return isNodeValid.result;
}

/**
 * Checks whether the title node meets the following conditions:
 *  - There is only one element that has the nodeType attribute set to TITLE
 *  - It has no parent nodes
 * @returns {boolean}
 */

export function validateTitleNode(templateXml, invalidNodes) {
    let isNodeValid;
    let title = $(templateXml).find('[nodeType=' + NodeTypeMapping.TITLE + ']');
    if (title && title.length === 1) {
        isNodeValid = isOnlyChildOfNodeType(title, NodeTypeMapping.NONE, templateXml);
    } else{
        isNodeValid = {result: false, error: errorMapping.DUPLICATED_NODE};
    }
    for (let i = 0; i < title.length; i++) {
        let node = $(title[i]);
        if (!isNodeValid.result) {
            highlightNode(node, invalidNodes);
        } else {
            revertNode(node);
        }
        $(node).attr("tooltip", isNodeValid.error);
    }
    return isNodeValid.result;

}


/**
 * Validates a group of nodes using the given validator
 * @param nodes Group on nodes of the same nodeType
 * @param nodeGroupValidator Function that applies a set of conditions to the node group
 * @returns {boolean} true if all nodes in the group meet the conditions, false otherwise
 */
export function validateNodeGroup(nodes, nodeGroupValidator, templateXml, invalidNodes) {
    let isNodeValid = {result: true, error: ""};
    let isGroupValid = true;
    for (let i = 0; i < nodes.length; i++) {
        let node = nodes[i];
        isNodeValid = nodeGroupValidator(node, templateXml);
        if (!isNodeValid.result) {
            highlightNode(node, invalidNodes);
            isGroupValid = false;
        } else {
            revertNode(node);
        }
        $(node).attr("tooltip", isNodeValid.error);
    }
    return isGroupValid;
}

/**
 * Checks if a node of type BASE meets the following conditions:
 * - It is a child of the root node
 * @param baseNode Node being validated
 * @returns {{result: boolean, error: string}} Validation result and error message in case of failure
 */
export function baseNodeValidator(baseNode, templateXml) {
    let isNodeValid = isOnlyChildOfNodeType(baseNode, NodeTypeMapping.ROOT, templateXml);
    if (isNodeValid.result) {
        isNodeValid = isOnlyNodeWithMapping(baseNode, BaseNodeMapping, templateXml);
    }
    return isNodeValid;
}

/**
 * Checks if a node of type INTERMEDIATE meets the following conditions:
 *  - It is a child of a BASE node or another INTERMEDIATE node
 * @param intermediateNode Node being validated
 * @returns {{result: boolean, error: string}} Validation result and error message in case of failure
 */
export function intermediateNodeValidator(intermediateNode, templateXml) {
    let isNodeValid = isOnlyChildOfNodeType(intermediateNode, NodeTypeMapping.BASE, templateXml);
    if (!isNodeValid.result) {
        isNodeValid = isOnlyChildOfNodeType(intermediateNode, NodeTypeMapping.INTERMEDIATE, templateXml);
    }
    return isNodeValid;
}

/**
 * Checks if a node of type DURATION meets the following conditions:
 *  - It is a child of a BASE node or an INTERMEDIATE node
 *  - It is placed inside a proper unit container
 * @param durationNode Node being validated
 * @returns {{result: boolean, error: string}} Validation result and error message in case of failure
 */
export function durationNodeValidator(durationNode, templateXml) {
    let isNodeValid = isOnlyChildOfNodeType(durationNode, NodeTypeMapping.INTERMEDIATE, templateXml);
    if (!isNodeValid.result) {
        isNodeValid = isOnlyChildOfNodeType(durationNode, NodeTypeMapping.BASE, templateXml);
    }
    if (isNodeValid.result) {
        isNodeValid = hasContainerParent(durationNode, templateXml);
    }
    if (isNodeValid.result) {
        isNodeValid = isDurationValue($(durationNode).attr("value"));
    }
    return isNodeValid;
}

/**
 * Checks if a node of type DATA meets the following conditions:
 *  - Is a child of any nodeType
 *  - It is placed inside a proper unit container
 *  - It contains a valid numeric value
 * @param dataNode Node being validated
 * @returns {{result: boolean, error: string}|{result: boolean, error: string}|{result: boolean, error: string}}
 */
export function dataNodeValidator(dataNode, templateXml) {
    let val = $(dataNode).attr("value");
    let isNodeValid = isOnlyChildOfNodeType(dataNode, NodeTypeMapping.ANY, templateXml);
    if (!isNodeValid.result) {
        return isNodeValid;
    }
    isNodeValid = hasContainerParent(dataNode, templateXml);
    if (!isNodeValid.result) {
        return isNodeValid;
    }
    isNodeValid = isDataValue(val);
    return isNodeValid;
}

export function isDataValue(val) {
    return (isFloat(val) ? {result: true, error: ""} : {result: false, error: errorMapping.INVALID_VALUE});
}

function isFloat(val) {
    let floatRegex = /^\d+(?:[.,]\d*?)?$/;
    return floatRegex.test(val);
}

export function isDurationValue(val) {
    return (greaterThan(val) || range(val) ? {result: true, error: ""} : {
        result: false,
        error: errorMapping.INVALID_VALUE
    });
}

function greaterThan(val) {
    let greaterThanRegex = /^>?\d+(?:[.,]\d*?)?$/;
    return greaterThanRegex.test(val)
}

function range(val) {
    let rangeRegex = /^\d+(?:[.,]\d*?)?(-\d+(?:[.,]\d*?)?)?$/;
    return rangeRegex.test(val)
}

export function getJsonData(templateXml) {
    const root = $(templateXml).find("[nodeType=root]");
    const generatedJson = {};
    buildJson(root, generatedJson, templateXml);
    generatedJson["title"] = $(templateXml).find(`[nodeType=${NodeTypeMapping.TITLE}]`)?.attr("label");
    return generatedJson;
}

function buildJson(node, generatedJson, xml) {
    let label = $(node).attr("label");
    let nodeType = $(node).attr("nodeType");
    if (nodeType === NodeTypeMapping.ROOT || nodeType === NodeTypeMapping.BASE) {
        label = $(node).attr("data");
    }
    /*if($(node).attr("nodeType") === NodeTypeMapping.DURATION || $(node).attr("nodeType") === NodeTypeMapping.DATA){
        let dataId = $(node).attr("data");
        generatedJson[dataId] = {value: label};
        //generatedJson[label][]
    } else {*/
    generatedJson[label] = {};
    let edges = $(xml).find("mxCell[source=" + $(node).attr("id") + "]");
    for (let i = 0; i < edges.length; i++) {
        let childNode = $(xml).find("object[id=" + $(edges[i]).attr("target") + "]");
        let type = $(childNode).attr("nodeType");
        if (type === NodeTypeMapping.DURATION || type === NodeTypeMapping.DATA) {
            if (i === 0) {
                generatedJson[label] = [];
            }
            let arrayElem = {}
            buildNodeData(arrayElem, childNode, xml);
            //buildJson(childNode, arrayElem, xml)
            generatedJson[label].push(arrayElem);
        } else {
            buildJson(childNode, generatedJson[label], xml)
        }

    }
    /*}*/
}

function buildNodeData(data, node, xml) {
    let nodeId = $(node).attr("id");
    let nodeType = $(node).attr("nodeType");
    let containerId = $(node).children(":first").attr("parent");
    let container = $(xml).find("[id=" + containerId + "]");
    let units = $(container).attr("units");
    let dataId = $(container).attr("data");
    let value = $(node).attr("value");
    if (nodeType === NodeTypeMapping.DATA) {
        data[dataId] = {nodeId: nodeId, value: parseFloat(value), units: units};
    } else {
        if (isFloat(value)) {
            data[dataId] = {nodeId: nodeId, min: parseFloat(value), max: parseFloat(value), units: units};
        } else if (greaterThan(value)) {
            data[dataId] = {
                nodeId: nodeId,
                min: parseFloat(value.trim().replace(">", "")),
                max: Number.POSITIVE_INFINITY,
                units: units
            };
        } else if (range(value)) {
            let parts = value.trim().split("-");
            data[dataId] = {nodeId: nodeId, min: parseFloat(parts[0]), max: parseFloat(parts[1]), units: units};
        }
    }
    let edges = $(xml).find("mxCell[source=" + $(node).attr("id") + "]");
    for (let i = 0; i < edges.length; i++) {
        let childNode = $(xml).find("object[id=" + $(edges[i]).attr("target") + "]");
        buildNodeData(data, childNode, xml);
    }

}